/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Fade,
    Flex,
    Stack,
    VisuallyHidden,
    Badge,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemBrand from '../item-variant/item-brand'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemPromotions from '../item-variant/item-promotion'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import QuantityPicker from '../../components/quantity-picker'
import DisplayPrice from '../display-price'
import {getPriceData} from '../../utils/product-utils'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import {getIsFixedPriceModel} from '../../esw/esw-helpers'
import useIsInternational from '../../hooks/use-is-international'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = (
    {
        product,
        primaryAction,
        onItemQuantityChange = noop,
        showLoading = false,
        removeAction,
        editAction,
        wishlistAction,
        isMiniCart
    },
    ref
) => {
    const {stepQuantity, showInventoryMessage, quantity, setQuantity} = useDerivedProduct(product)
    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()
    const config = getConfig()
    const isFixedPriceModel = getIsFixedPriceModel()
    const isInternational = useIsInternational()

    const isGreyedOut = !product?.inventory?.orderable
    const styles = useMultiStyleConfig('Cart')

    const priceData = useMemo(() => {
        // Check if product has only approaching discount promotion
        const hasOnlyApproachingPromotion =
            product?.productPromotions?.length === 1 &&
            product.productPromotions[0]?.promotionId?.includes('approaching-discount')

        // If product has basket pricing (itemId exists), use basket prices instead of recalculating
        if (product?.itemId) {
            const quantity = product.quantity || 1
            const fixedPrice = product.basePrice ?? product.price / quantity

            if (isFixedPriceModel) {
                return {
                    currentPrice: fixedPrice,
                    listPrice: fixedPrice,
                    pricePerUnit: fixedPrice,
                    isASet: product?.type?.set,
                    isMaster: product?.type?.master,
                    isRange: product?.type?.set || product?.type?.master || false,
                    isOnSale: false,
                    isFixedPriceModel: true,
                    hasPromotion: false,
                    hasOnlyApproachingPromotion
                }
            }

            // Calculate current price per unit considering discounts
            let currentPrice = product.basePrice

            // If there are price adjustments, use priceAfterItemDiscount to get the actual discounted price
            if (
                product?.priceAdjustments &&
                product.priceAdjustments.length > 0 &&
                product.priceAfterItemDiscount
            ) {
                // Calculate the discounted price per unit
                currentPrice = product.priceAfterItemDiscount / quantity
            }

            // Get list price: prioritize c_listPrice from backend, then tieredPrices, then product.price
            const listPriceTier = product?.tieredPrices?.find((tier) =>
                tier.pricebook?.includes('list_price')
            )
            const listPrice = product?.c_listPrice || listPriceTier?.price || product.price

            return {
                currentPrice: currentPrice,
                listPrice: listPrice,
                pricePerUnit: currentPrice,
                isASet: product?.type?.set,
                isMaster: product?.type?.master,
                isRange: product?.type?.set || product?.type?.master || false,
                isOnSale: listPrice > currentPrice,
                isFixedPriceModel: false,
                hasPromotion: product?.productPromotions && product?.productPromotions?.length > 0,
                hasOnlyApproachingPromotion
            }
        }
        // Otherwise use the standard price calculation
        const standardPriceData = getPriceData(product, {quantity})
        return {
            ...standardPriceData,
            hasOnlyApproachingPromotion
        }
    }, [isFixedPriceModel, product, quantity])

    const promotionId = useMemo(() => {
        return product?.productPromotions?.[0]?.promotionId
    }, [product?.productPromotions])

    const promotionsQuery = usePromotions(
        {
            parameters: {ids: promotionId}
        },
        {
            enabled: !isInternational && !isFixedPriceModel && !!promotionId
        }
    )

    let enableFinalSaleLabel =
        !isInternational &&
        (config.app.ENABLE_FINAL_SALE_LABEL === true ||
            config.app.ENABLE_FINAL_SALE_LABEL === 'true' ||
            product?.c_isEGiftCard ||
            product?.c_isGiftCard)

    const promotionData = promotionsQuery?.data?.data?.[0] || {}

    const renderPromotion = () => {
        if (isInternational) return null
        if (isFixedPriceModel) return null

        // Only show promotion if there's actual evidence it's applied:
        // 1. Price discount (priceAfterItemDiscount/priceAfterOrderDiscount < price)
        // 2. Price adjustments exist
        // 3. Other indicators of applied promotions
        const hasPriceDiscount =
            (product?.priceAfterItemDiscount && product.priceAfterItemDiscount < product?.price) ||
            (product?.priceAfterOrderDiscount && product.priceAfterOrderDiscount < product?.price)

        const hasPriceAdjustments = product?.priceAdjustments?.length > 0

        const isPromotionApplied = hasPriceDiscount || hasPriceAdjustments

        if (!isPromotionApplied || !promotionData.c_showCalloutMessage || !promotionData.name)
            return null

        return (
            <Text fontSize="sm" _empty={{display: 'none'}}>
                {promotionData?.name}
            </Text>
        )
    }

    const isGiftCard = product?.c_isGiftCard || product?.c_isEGiftCard

    return (
        <Box
            position="relative"
            data-testid={`sf-cart-item-${product.productId ? product.productId : product.id}`}
            ref={ref}
            {...styles.productItem}
            minWidth={0}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                <Stack align="flex-start" width="100%">
                    <Flex
                        width="full"
                        alignItems={isMiniCart ? 'flex-start' : 'stretch'}
                        backgroundColor="white"
                        minWidth={0}
                    >
                        <CartItemVariantImage
                            width="120px"
                            height="100%"
                            mr={4}
                            ratio={2 / 3}
                            imageProps={isGreyedOut && {opacity: '0.5'}}
                            isGiftCard={isGiftCard}
                        />
                        <Stack
                            spacing={0}
                            flex={'1.06'}
                            minWidth={0}
                            alignItems="flex-start"
                            justifyContent="space-between"
                            mr={{base: '0', lg: '30px'}}
                        >
                            <Stack
                                flex={isMiniCart ? 1 : 0}
                                spacing={0}
                                minWidth={0}
                                width="100%"
                                opacity={isGreyedOut ? '0.5' : '1'}
                            >
                                <Box
                                    minHeight={
                                        product?.c_limitedQuantity && !isGreyedOut
                                            ? '30px'
                                            : undefined
                                    }
                                    mb={
                                        product?.c_limitedQuantity && !isGreyedOut ? '3' : undefined
                                    }
                                >
                                    {product?.c_limitedQuantity && !isGreyedOut && (
                                        <Fade in={true}>
                                            <Badge
                                                px="3"
                                                py="1"
                                                background="pink.900"
                                                color="white"
                                                fontSize="xs"
                                                lineHeight="18px"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="A few items left"
                                                    id="cart_badge.few_items_left_title"
                                                />
                                            </Badge>
                                        </Fade>
                                    )}
                                </Box>
                                <CartItemBrand brand={product.brand} />
                                <CartItemVariantName />
                                {!isMiniCart && (
                                    <CartItemVariantAttributes
                                        quantity={quantity}
                                        opacity={isGreyedOut ? '0.5' : '1'}
                                    />
                                )}
                                {isMiniCart ? (
                                    <>
                                        <CartItemVariantAttributes
                                            opacity={isGreyedOut ? '0.5' : '1'}
                                        />
                                        <Box height="100%">
                                            <Text fontSize="sm">
                                                <DisplayPrice
                                                    isDisplayPricePerUnit={true}
                                                    quantity={quantity}
                                                    priceData={priceData}
                                                    currency={activeCurrency}
                                                    showQuantity={true}
                                                    currentPriceProps={{
                                                        fontWeight: 'normal'
                                                    }}
                                                    listPriceProps={{
                                                        fontWeight: 'normal'
                                                    }}
                                                />
                                            </Text>

                                            {renderPromotion()}

                                            {product?.c_isFinalSale && enableFinalSaleLabel && (
                                                <Text
                                                    color="gray.700"
                                                    fontSize="14px"
                                                    lineHeight="18px"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Final Sale*"
                                                        id="display_price.label.final_sale"
                                                    />
                                                </Text>
                                            )}
                                        </Box>
                                    </>
                                ) : (
                                    <HideOnDesktop>
                                        <Box>
                                            <DisplayPrice
                                                priceData={priceData}
                                                currency={activeCurrency}
                                                currentPriceProps={{
                                                    fontSize: 'sm',
                                                    fontWeight: 'normal'
                                                }}
                                                listPriceProps={{
                                                    fontSize: 'sm',
                                                    fontWeight: 'normal'
                                                }}
                                            />

                                            {renderPromotion()}

                                            {product?.c_isFinalSale && enableFinalSaleLabel && (
                                                <Text
                                                    color="gray.700"
                                                    fontSize="14px"
                                                    lineHeight="18px"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Final Sale*"
                                                        id="display_price.label.final_sale"
                                                    />
                                                </Text>
                                            )}
                                        </Box>
                                        {!isGiftCard && (
                                            <QuantityPicker
                                                opacity={isGreyedOut ? '0.5' : '1'}
                                                my={5}
                                                step={stepQuantity}
                                                value={quantity}
                                                min={1}
                                                max={product?.c_productMaxOrderQty}
                                                clampValueOnBlur={false}
                                                onBlur={(e) => {
                                                    // Default to last known quantity if a user leaves the box with an invalid value
                                                    const {value} = e.target
                                                    if (!value) {
                                                        setQuantity(product.quantity)
                                                    }
                                                }}
                                                onChange={(stringValue, numberValue) => {
                                                    // Set the Quantity of product to value of input if value number
                                                    if (numberValue >= 0) {
                                                        // Call handler
                                                        onItemQuantityChange(numberValue).then(
                                                            (isValidChange) =>
                                                                isValidChange &&
                                                                setQuantity(numberValue)
                                                        )
                                                    } else if (stringValue === '') {
                                                        // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                        // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                                        setQuantity(stringValue)
                                                    }
                                                }}
                                                productName={product?.name}
                                            />
                                        )}
                                        <VisuallyHidden role="status">
                                            {product?.name}
                                            {intl.formatMessage(
                                                {
                                                    id: 'item_variant.assistive_msg.quantity',
                                                    defaultMessage: 'Quantity {quantity}'
                                                },
                                                {
                                                    quantity: product?.quantity
                                                }
                                            )}
                                        </VisuallyHidden>
                                    </HideOnDesktop>
                                )}
                            </Stack>
                            {isGreyedOut && product?.inventory && (
                                <Box
                                    background="alternativeWarning.100"
                                    display="flex"
                                    width="249px"
                                    maxWidth={{base: '170px', lg: 'none'}}
                                    padding="8px 16px"
                                    alignItems="center"
                                    color="warningText"
                                    my={2}
                                >
                                    <Text fontSize="sm" lineHeight="15px">
                                        <FormattedMessage
                                            defaultMessage="This item is no longer available and won't be added to your order."
                                            id="product_item.message.no_longer_available"
                                        />
                                    </Text>
                                </Box>
                            )}
                            {isMiniCart && <Stack alignItems="flex-start">{wishlistAction}</Stack>}
                            {editAction}
                        </Stack>
                        <Flex flex={isMiniCart ? 0 : 1} display={{base: 'none', lg: 'flex'}}>
                            <Stack
                                align="flex-start"
                                justify="space-between"
                                h={'full'}
                                maxWidth={'180px'}
                            >
                                <Stack spacing={1}>
                                    {!isMiniCart && (
                                        <Box>
                                            {!isGiftCard && (
                                                <QuantityPicker
                                                    opacity={isGreyedOut ? '0.5' : '1'}
                                                    mb="3"
                                                    step={stepQuantity}
                                                    value={quantity}
                                                    min={1}
                                                    max={product?.c_productMaxOrderQty}
                                                    clampValueOnBlur={false}
                                                    onBlur={(e) => {
                                                        // Default to last known quantity if a user leaves the box with an invalid value
                                                        const {value} = e.target
                                                        if (!value) {
                                                            setQuantity(product.quantity)
                                                        }
                                                    }}
                                                    onChange={(stringValue, numberValue) => {
                                                        // Set the Quantity of product to value of input if value number
                                                        if (numberValue >= 0) {
                                                            // Call handler
                                                            onItemQuantityChange(numberValue).then(
                                                                (isValidChange) =>
                                                                    isValidChange &&
                                                                    setQuantity(numberValue)
                                                            )
                                                        } else if (stringValue === '') {
                                                            // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                            // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                                            setQuantity(stringValue)
                                                        }
                                                    }}
                                                    productName={product?.name}
                                                    showInventoryMessage={showInventoryMessage}
                                                />
                                            )}
                                            <VisuallyHidden role="status">
                                                {product?.name}
                                                {intl.formatMessage(
                                                    {
                                                        id: 'item_variant.assistive_msg.quantity',
                                                        defaultMessage: 'Quantity {quantity}'
                                                    },
                                                    {
                                                        quantity: product?.quantity
                                                    }
                                                )}
                                            </VisuallyHidden>

                                            <DisplayPrice
                                                priceData={priceData}
                                                currency={activeCurrency}
                                                currentPriceProps={{
                                                    fontWeight: 'normal'
                                                }}
                                                listPriceProps={{
                                                    fontWeight: 'normal'
                                                }}
                                            />

                                            {renderPromotion()}

                                            {product?.c_isFinalSale && enableFinalSaleLabel && (
                                                <Text
                                                    color="gray.700"
                                                    fontSize="14px"
                                                    lineHeight="18px"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Final Sale*"
                                                        id="display_price.label.final_sale"
                                                    />
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                    <Box display={['none', 'block', 'block', 'block']}>
                                        {primaryAction}
                                    </Box>
                                </Stack>
                                {!isMiniCart && <Stack>{wishlistAction}</Stack>}
                            </Stack>
                        </Flex>
                        <Stack
                            justifyContent="space-between"
                            alignItems="flex-end"
                            lineHeight={'normal'}
                        >
                            {removeAction}
                            {!isMiniCart && <HideOnDesktop>{wishlistAction}</HideOnDesktop>}
                        </Stack>
                    </Flex>

                    <Box display={['block', 'none', 'none', 'none']} w={'full'}>
                        {primaryAction}
                    </Box>
                </Stack>
            </ItemVariantProvider>
        </Box>
    )
}
export default forwardRef(ProductItem)

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    removeAction: PropTypes.node,
    editAction: PropTypes.node,
    wishlistAction: PropTypes.node,
    isMiniCart: PropTypes.bool
}
